import { memo } from 'react';
import { throttle } from 'lodash';
import { useTranslation } from 'react-i18next';
import api from '@shared/api';
import routes from '@root/routes';
import { Select, TextArea } from '@components/shared/forms';
import { AsyncSelect } from '@components/shared/forms/Dropdowns';
import Input from './Input';
import css from './styles.module.less';
import CustomIcon from '../../../../shared/CustomIcon';

const fetchCompanies = async (query, page) => {
  const response = await api.fetch(routes.api.admin.companies(query, page));
  const { companies } = await response.json();
  return companies.map(company => ({ label: company.name, value: company.id }));
};
const searchCompanies = throttle(query => fetchCompanies(query, 1), 250);

const Details = ({
  owningCompanyId,
  owningCompanyName,
  notableTenants,
  rentableBuildingAreaUnit,
  ceilingHeightUnits,
  initialValues,
}) => {
  const { t: adminT } = useTranslation('admin');
  const { t: commonT } = useTranslation('common');

  return (
    <section>
      <h2>{adminT('building.details.title')}</h2>
      <div className={css.sideBySide}>
        <Input
          containerClass={css.left}
          labelText={adminT('building.units')}
          name="units"
          disabled
        />
        <Input
          containerClass={css.right}
          labelText={adminT('building.currency')}
          name="currency"
          disabled
        />
      </div>
      <Select
        className={css.select}
        required
        labelText={adminT('building.ceilingHeightUnits.displayName')}
        data-testid="building-ceiling-height-units"
        name="ceilingHeightUnits"
        isClearable={false}
        options={[
          { label: adminT('building.ceilingHeightUnits.imperial'), value: 'imperial' },
          { label: adminT('building.ceilingHeightUnits.metric'), value: 'metric' },
        ]}
        placeholder=""
        value={ceilingHeightUnits}
      />
      {initialValues.ceilingHeightUnits !== ceilingHeightUnits && (
        <div className={css.warningMessage}>
          <CustomIcon type="exclamation-circle" className={css.warningIcon}></CustomIcon>
          {adminT('building.ceilingHeightUnits.warningMessage')}
        </div>
      )}

      <AsyncSelect
        className={css.select}
        data-testid="building-space-provider"
        initialValue={{
          label: owningCompanyName,
          value: owningCompanyId,
        }}
        labelText={adminT('building.spaceProvider')}
        loadOptions={searchCompanies}
        mapValue={v => v.value}
        name="owningCompanyId"
        placeholder=""
      />
      <Input
        containerClass={css.half}
        labelText={adminT('building.numberOfFloors')}
        name="numberOfFloors"
        type="number"
      />
      <div className={css.sideBySide} data-testid="rentable-building-area">
        <Input
          containerClass={css.left}
          labelText={adminT('building.rentableBuildingArea')}
          name="rentableBuildingArea"
          type="number"
        />
        <Select
          className={css.select}
          isClearable={false}
          labelClass={css.invisible}
          labelText={commonT('unit')}
          name="rentableBuildingAreaUnit"
          options={[
            {
              label: adminT('units:SquareFoot'),
              value: 'SquareFoot',
            },
            {
              label: adminT('units:SquareMeter'),
              value: 'SquareMeter',
            },
          ]}
          placeholder=""
          value={rentableBuildingAreaUnit}
          isDisabled
        />
      </div>
      <div className={css.sideBySide}>
        <Input
          containerClass={css.left}
          labelText={adminT('building.yearBuilt')}
          name="yearBuilt"
          type="number"
        />
        <Input
          containerClass={css.right}
          labelText={adminT('building.yearRenovated')}
          name="yearRenovated"
          type="number"
        />
      </div>
      <TextArea labelText={adminT('building.description')} name="description" />
      <AsyncSelect
        className={css.select}
        data-testid="notable-tenants"
        initialValue={notableTenants}
        isMulti
        labelText={adminT('building.notableTenants')}
        loadOptions={searchCompanies}
        name="notableTenants"
        mapValue={id => ({ companyId: id })}
        placeholder=""
      />
    </section>
  );
};

export default memo(Details);
